<script lang="ts" setup>
import { computed } from 'vue';

import { container } from '@/container';
import { DarkMode } from '@/services/DarkMode';

const darkMode = container.get(DarkMode);

const isDark = computed({
  get: () => darkMode.isDark,
  set: (value: boolean) => darkMode.set(value),
});

const toggle = () => darkMode.toggle();
</script>

<template>
  <button
    v-if="darkMode.isAvailable"
    class="dark-mode-toggle"
    :class="{ 'dark-mode-toggle--dark': isDark }"
    :title="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
    @click="toggle"
  >
    <span class="dark-mode-toggle__icon">
      <svg
        v-if="isDark"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        width="20"
        height="20"
      >
        <!-- eslint-disable max-len -->
        <path d="M12 3a9 9 0 1 0 9 9c0-.46-.04-.92-.1-1.36a5.389 5.389 0 0 1-4.4 2.26 5.403 5.403 0 0 1-3.14-9.8c-.44-.06-.9-.1-1.36-.1z" />
        <path
          d="M15.5 5.5l.5-1.5.5 1.5L18 6l-1.5.5-.5 1.5-.5-1.5L14 6l1.5-.5z"
          fill="currentColor"
        />
        <path
          d="M19 3l.5-1 .5 1 1 .5-1 .5-.5 1-.5-1-1-.5 1-.5z"
          fill="currentColor"
        />
        <!-- eslint-enable max-len -->
      </svg>
      <svg
        v-else
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        width="20"
        height="20"
      >
        <circle
          cx="12"
          cy="12"
          r="5"
        />
        <!-- eslint-disable max-len -->
        <path
          d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          fill="none"
        />
        <!-- eslint-enable max-len -->
      </svg>
    </span>
  </button>
</template>

<style lang="scss" scoped>
.dark-mode-toggle {
  align-items: center;
  background-color: #ffffff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  margin-left: 0.5em;
  padding: 0;
  transition: background-color 150ms ease, box-shadow 150ms ease;
  width: 32px;

  &:hover {
    box-shadow: 0 0 0 2px #4a90c8;
  }

  &:focus-visible {
    outline: 2px solid var(--focus-color, #4a90c8);
    outline-offset: 2px;
  }

  &__icon {
    color: #F59E0B;
    display: flex;
    line-height: 1;
  }

  // Dark mode active state
  &--dark {
    background-color: #1C1D1E;

    &:hover {
      box-shadow: 0 0 0 2px #4a90c8;

      .dark-mode-toggle__icon {
        background-color: #1a3a5a;
        border-radius: 50%;
      }
    }

    .dark-mode-toggle__icon {
      color: #F59E0B;
    }
  }
}
</style>
